import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { adminApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Card, CardBody, CardHeader } from '../../components/ui/Card'
import { ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { Input, Toggle } from '../../components/ui/Input'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { ESTADO_FIPE_LABEL, EstadoFipe } from '../../lib/enums'
import { formatDate } from '../../lib/utils'
import type { ConfiguracaoFipe, MonitorFipe } from '../../types'

const EIXO = { fontSize: 11, fill: '#8B949E' }
const TOOLTIP = { background: '#1B2330', border: '1px solid #2D3748', borderRadius: 6, fontSize: 12 }
const numero = new Intl.NumberFormat('pt-BR')

const ESTADO_VARIANTE = { [EstadoFipe.Ativa]: 'income', [EstadoFipe.Desligada]: 'expense', [EstadoFipe.SemAtualizacaoMensal]: 'warning' } as const

/** Monitor da FIPE: uso da cota, cache, erros, e as chaves que o admin pode mexer. */
export function MonitorFipePage() {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['admin-fipe-monitor'], queryFn: adminApi.fipeMonitor, refetchInterval: 60_000 })

  return (
    <>
      <PageHeader title="Monitor da FIPE" />
      {isLoading ? <Card><LoadingState /></Card> : isError || !data ? <Card><ErrorState message={(error as Error)?.message || 'Não foi possível carregar o monitor.'} /></Card> : <Painel m={data} />}
    </>
  )
}

function Painel({ m }: { m: MonitorFipe }) {
  const c = m.consultasHoje
  const serie = m.serie.map((d) => ({ ...d, dia: formatDate(d.data).slice(0, 5), live: Math.max(0, d.total - d.totalJob) }))
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant={ESTADO_VARIANTE[m.estado]} className="normal-case">Estado: {ESTADO_FIPE_LABEL[m.estado]}</Badge>
        <p className="text-13 text-muted">Fonte: Parallelum (fipe.online) · cache próprio no Walleon</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Cartao rotulo="Consultas hoje" valor={`${numero.format(c.total)} / ${numero.format(c.limite)}`} sub={`Atualização mensal usa até ${numero.format(c.orcamentoJob)} (hoje: ${numero.format(c.totalJob)})`} />
        <Cartao rotulo="Acertos do cache" valor={m.acertosCachePercentual != null ? `${Math.round(m.acertosCachePercentual)}%` : '—'} sub="últimos 30 dias" />
        <Cartao rotulo="Erros hoje" valor={numero.format(m.errosHoje)} sub={m.errosHoje > 0 ? 'falhas na fonte' : 'sem falhas'} tom={m.errosHoje > 0 ? 'text-expense' : undefined} />
        <Cartao rotulo="Idade média do valor" valor={m.idadeMediaValorDias != null ? `${Math.round(m.idadeMediaValorDias)} dias` : '—'} sub="só veículos em uso" />
        <Cartao rotulo="Modelos em uso" valor={numero.format(m.modelosEmUso)} sub={`${numero.format(m.modelosSemValor)} sem valor`} tom={m.modelosSemValor > 0 ? 'text-warning' : undefined} />
      </div>

      <Card>
        <CardHeader title="Consultas por dia (últimos 14 dias)" icon="ri-bar-chart-line" />
        <CardBody>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={serie} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
              <XAxis dataKey="dia" tick={EIXO} axisLine={false} tickLine={false} />
              <YAxis tick={EIXO} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP} cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                formatter={(valor) => [numero.format(Number(valor)), 'Consultas']}
                labelFormatter={(_, itens) => {
                  const d = itens?.[0]?.payload as (typeof serie)[number] | undefined
                  return d ? `${formatDate(d.data)} · job ${d.totalJob} · erros ${d.erros}${d.corteAcionado ? ' · corte acionado' : ''}` : ''
                }} />
              <Bar dataKey="total" name="Consultas" radius={[3, 3, 0, 0]} maxBarSize={32}>
                {serie.map((d) => <Cell key={d.data} fill={d.corteAcionado ? '#F59E0B' : '#FFB300'} fillOpacity={d.corteAcionado ? 1 : 0.7} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="mt-2 text-xs text-muted">Barra âmbar forte: dia em que o corte automático guardou consultas para o uso ao vivo.</p>
        </CardBody>
      </Card>

      <Controles key={m.configuracao.updatedAt ?? 'inicial'} config={m.configuracao} />

      <p className="rounded bg-warning/10 px-3 py-2.5 text-13 text-warning">
        <i className="ri-error-warning-line" aria-hidden /> Pendência: autorização de uso da tabela ainda não pedida à FIPE e à Parallelum.
      </p>
    </div>
  )
}

function Cartao({ rotulo, valor, sub, tom }: { rotulo: string; valor: string; sub: string; tom?: string }) {
  return (
    <Card>
      <CardBody>
        <p className="text-2xs uppercase text-muted">{rotulo}</p>
        <p className={`mt-1 text-2xl font-semibold ${tom ?? 'text-white'}`}>{valor}</p>
        <p className="mt-0.5 text-xs text-muted">{sub}</p>
      </CardBody>
    </Card>
  )
}

function Controles({ config }: { config: ConfiguracaoFipe }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [ligada, setLigada] = useState(config.ligada)
  const [mensal, setMensal] = useState(config.atualizacaoMensalLigada)
  const [limite, setLimite] = useState(String(config.limiteDiario))
  const [porUsuario, setPorUsuario] = useState(String(config.limitePorUsuarioDia))
  const [percentual, setPercentual] = useState(String(config.percentualOrcamentoJob))

  const lim = Number(limite)
  const usu = Number(porUsuario)
  const pct = Number(percentual)
  const erros = {
    limite: !Number.isInteger(lim) || lim < 1 || lim > 100000 ? 'Informe um número inteiro de 1 a 100.000.' : undefined,
    porUsuario: !Number.isInteger(usu) || usu < 0 || (Number.isInteger(lim) && usu > lim) ? 'Informe de 0 até o limite diário.' : undefined,
    percentual: !Number.isFinite(pct) || pct < 0 || pct > 100 || percentual.trim() === '' ? 'Informe de 0 a 100.' : undefined,
  }
  const valido = !erros.limite && !erros.porUsuario && !erros.percentual

  const salvar = useMutation({
    mutationFn: () => adminApi.salvarFipeConfiguracao({ ligada, atualizacaoMensalLigada: mensal, limiteDiario: lim, limitePorUsuarioDia: usu, percentualOrcamentoJob: pct }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-fipe-monitor'] }); toast.success('Configuração da FIPE salva.') },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Card>
      <CardHeader title="Controles" icon="ri-settings-3-line" />
      <CardBody className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><p className="text-13 font-medium text-white">FIPE ligada (chave global)</p><p className="text-xs text-muted">Desligada: o app usa só o cache e o cadastro manual.</p></div>
          <Toggle label={ligada ? 'Ligada' : 'Desligada'} checked={ligada} onChange={setLigada} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/70 pt-5">
          <div><p className="text-13 font-medium text-white">Atualização mensal dos veículos em uso</p><p className="text-xs text-muted">Lotes diários dentro do orçamento. Desligue se a fonte der problema.</p></div>
          <Toggle label={mensal ? 'Ligada' : 'Desligada'} checked={mensal} onChange={setMensal} disabled={!ligada} />
        </div>
        <div className="grid gap-3 border-t border-border/70 pt-5 sm:grid-cols-3">
          <Input label="Limite diário da fonte" type="number" inputMode="numeric" min={1} value={limite} error={erros.limite} onChange={(e) => setLimite(e.target.value)} />
          <Input label="Limite por usuário por dia" type="number" inputMode="numeric" min={0} value={porUsuario} error={erros.porUsuario} onChange={(e) => setPorUsuario(e.target.value)} />
          <Input label="Orçamento do job mensal (%)" type="number" inputMode="numeric" min={0} max={100} value={percentual} error={erros.percentual} onChange={(e) => setPercentual(e.target.value)} />
        </div>
        <p className="text-xs text-muted">A Parallelum libera 500 consultas por dia (1.000 com token gratuito). O job mensal usa só a parte do limite definida em %; o resto fica para consultas ao vivo.</p>
        <div className="flex justify-end"><Button icon="ri-save-line" disabled={!valido} loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar controles</Button></div>
      </CardBody>
    </Card>
  )
}
